/**
 * SelectTool - 피처 선택 및 수정 도구
 */

import Select from 'ol/interaction/Select';
import Modify from 'ol/interaction/Modify';
import DragBox from 'ol/interaction/DragBox';
import DragPan from 'ol/interaction/DragPan';
import { click, shiftKeyOnly, platformModifierKeyOnly } from 'ol/events/condition';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { mapManager } from '../core/MapManager.js';
import { layerManager } from '../core/LayerManager.js';
import { eventBus, Events } from '../utils/EventBus.js';
import { pickOrphanFeatures } from './selectionCleanup.js';

// 선택 스타일
const SELECT_STYLE = new Style({
  fill: new Fill({
    color: 'rgba(255, 193, 7, 0.3)'
  }),
  stroke: new Stroke({
    color: '#ffc107',
    width: 3
  }),
  image: new CircleStyle({
    radius: 8,
    fill: new Fill({ color: '#ffc107' }),
    stroke: new Stroke({ color: '#ffffff', width: 2 })
  })
});

class SelectTool {
  constructor() {
    this.map = null;
    this.select = null;
    this.modify = null;
    this.dragBox = null;
    this.dragPan = null;
    this.isActive = false;
    this.selectedFeatures = null;

    // 레이어를 지우면 그 안에 있던 피처는 선택 상태로 남을 수 없다.
    // 선택이 줄면 SELECTION_CHANGED 가 나가므로 툴바 버튼과 속성 카드가 함께 정리된다.
    eventBus.on(Events.LAYER_REMOVED, () => this.dropRemovedFeatures());

    // 합치기를 되돌리면 합쳐졌던 피처가 사라지는데, 선택에 남아 있으면
    // Select 인터랙션이 제 오버레이에 그대로 그린다 — 되살아난 원본 위에 겹친 유령이 된다.
    // (합치는 시점에는 결과가 레이어 안에 있으므로 아무 일도 하지 않는다)
    eventBus.on(Events.FEATURES_MERGED, () => this.dropRemovedFeatures());
  }

  /**
   * 사라진 레이어의 피처를 선택에서 뺀다.
   * 삭제 시점엔 그 레이어가 이미 layerManager 에서 빠진 뒤라,
   * 남은 레이어 어디에도 없는 피처를 골라 내는 방식으로 찾는다.
   */
  dropRemovedFeatures() {
    if (!this.selectedFeatures) return;

    const orphans = pickOrphanFeatures(
      this.selectedFeatures.getArray(),
      layerManager.getAllLayers()
    );

    orphans.forEach((feature) => this.selectedFeatures.remove(feature));
  }

  /**
   * 초기화
   */
  init() {
    this.map = mapManager.getMap();
  }

  /**
   * 선택 도구 활성화
   * @param {Object} options - 옵션
   */
  activate(options = {}) {
    if (!this.map) this.init();

    const { enableModify = true } = options;

    // 기존 인터랙션 제거
    this.deactivate();

    // Select 인터랙션 생성
    this.select = new Select({
      condition: click,
      toggleCondition: shiftKeyOnly, // Shift+클릭으로 다중 선택
      multi: true, // 다중 선택 허용
      style: SELECT_STYLE,
      // 배경지도 레이어 제외
      filter: (feature, layer) => {
        if (!layer) return false;
        const props = layer.getProperties();
        return props.type !== 'base';
      }
    });

    this.selectedFeatures = this.select.getFeatures();

    // 선택 변경 이벤트
    this.select.on('select', (event) => {
      this.handleSelect(event);
    });

    // 선택 집합이 바뀔 때마다(클릭·박스·삭제 등) 알림 → 툴바 버튼 토글
    this.selectedFeatures.on('add', () => this.emitSelectionChange());
    this.selectedFeatures.on('remove', () => this.emitSelectionChange());

    this.map.addInteraction(this.select);

    // 드래그 박스 선택: 선택 도구가 활성일 때 빈 곳을 드래그하면 범위 내 피처 일괄 선택
    // 지도 패닝과 충돌하므로 선택 도구 활성 동안 DragPan을 비활성화
    this.map.getInteractions().forEach((interaction) => {
      if (interaction instanceof DragPan) {
        this.dragPan = interaction;
        interaction.setActive(false);
      }
    });

    this.dragBox = new DragBox();

    this.dragBox.on('boxend', (event) => {
      const extent = this.dragBox.getGeometry().getExtent();
      const additive = !!(event.mapBrowserEvent &&
        event.mapBrowserEvent.originalEvent &&
        event.mapBrowserEvent.originalEvent.shiftKey);

      if (!additive) {
        this.selectedFeatures.clear();
      }

      const existing = this.selectedFeatures.getArray();
      layerManager.getAllLayers().forEach((layerInfo) => {
        const olLayer = layerInfo.olLayer;
        if (!olLayer) return;
        if (olLayer.getProperties().type === 'base') return;
        if (!olLayer.getVisible()) return;
        if (!layerInfo.source) return; // 흐름·래스터 레이어는 벡터 source 가 없다
        layerInfo.source.forEachFeatureIntersectingExtent(extent, (feature) => {
          if (!existing.includes(feature)) {
            this.selectedFeatures.push(feature);
          }
        });
      });
    });

    this.map.addInteraction(this.dragBox);

    // Modify 인터랙션 추가 (수정 모드)
    if (enableModify) {
      this.modify = new Modify({
        features: this.selectedFeatures,
        // Shift 키를 누르고 꼭짓점 클릭 시 삭제
        deleteCondition: (event) => {
          return platformModifierKeyOnly(event) && click(event);
        }
      });

      this.modify.on('modifystart', (event) => {
        this.handleModifyStart(event);
      });

      this.modify.on('modifyend', (event) => {
        this.handleModifyEnd(event);
      });

      this.map.addInteraction(this.modify);
    }

    this.isActive = true;

    // 상태 메시지
    const statusEl = document.getElementById('status-message');
    if (statusEl) {
      statusEl.textContent = '피처를 클릭하여 선택 (Shift+클릭: 다중 선택)';
    }

    // 키보드 이벤트 등록 (Delete 키로 삭제)
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);

    eventBus.emit(Events.TOOL_ACTIVATED, { tool: 'select' });
  }

  /**
   * 선택 집합 변경 알림 (툴바의 선택취소/삭제 버튼 토글용)
   */
  emitSelectionChange() {
    const count = this.selectedFeatures ? this.selectedFeatures.getLength() : 0;
    eventBus.emit(Events.SELECTION_CHANGED, { count });
  }

  /**
   * 선택 이벤트 처리
   */
  handleSelect(event) {
    const selected = event.selected;
    const deselected = event.deselected;
    const totalSelected = this.selectedFeatures ? this.selectedFeatures.getLength() : 0;

    if (selected.length > 0) {
      const feature = selected[0];
      const geometry = feature.getGeometry();

      eventBus.emit(Events.FEATURE_SELECTED, {
        feature,
        geometryType: geometry.getType(),
        count: totalSelected
      });

      // 상태 메시지 업데이트
      const statusEl = document.getElementById('status-message');
      if (statusEl) {
        if (totalSelected > 1) {
          statusEl.textContent = `${totalSelected}개 선택됨 (Delete로 삭제)`;
        } else {
          statusEl.textContent = `선택됨 (Delete로 삭제, 꼭짓점 드래그로 수정)`;
        }
      }
    }

    if (deselected.length > 0) {
      eventBus.emit(Events.FEATURE_DESELECTED, {
        features: deselected
      });

      if (totalSelected === 0) {
        const statusEl = document.getElementById('status-message');
        if (statusEl) {
          statusEl.textContent = '피처를 클릭하여 선택 (Shift+클릭: 다중 선택)';
        }
      } else {
        const statusEl = document.getElementById('status-message');
        if (statusEl) {
          if (totalSelected > 1) {
            statusEl.textContent = `${totalSelected}개 선택됨 (Delete로 삭제)`;
          } else {
            statusEl.textContent = `선택됨 (Delete로 삭제, 꼭짓점 드래그로 수정)`;
          }
        }
      }
    }
  }

  /**
   * 수정 완료 이벤트 처리
   */
  /**
   * 수정 시작 이벤트 처리
   */
  handleModifyStart(event) {
    const features = event.features.getArray();
    features.forEach(feature => {
      // 수정할 피처가 속한 레이어 찾기
      const layers = layerManager.getAllLayers();
      for (const layerInfo of layers) {
        if (!layerInfo.source) continue; // 흐름·래스터 레이어는 벡터 source 가 없다
        if (layerInfo.source.hasFeature(feature)) {
          eventBus.emit(Events.FEATURE_MODIFY_START, {
            feature,
            layerId: layerInfo.id
          });
          break;
        }
      }
    });
  }

  /**
   * 수정 완료 이벤트 처리
   */
  handleModifyEnd(event) {
    const features = event.features.getArray();

    features.forEach(feature => {
      eventBus.emit(Events.FEATURE_MODIFIED, { feature });
    });

    // 상태 메시지
    const statusEl = document.getElementById('status-message');
    if (statusEl) {
      statusEl.textContent = '도형이 수정되었습니다';
      setTimeout(() => {
        statusEl.textContent = '피처를 클릭하여 선택하세요';
      }, 2000);
    }
  }

  /**
   * 키보드 이벤트 처리
   */
  handleKeyDown(event) {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      this.deleteSelectedFeatures();
    } else if (event.key === 'Escape') {
      this.clearSelection();
    }
  }

  /**
   * 선택된 피처 삭제
   */
  deleteSelectedFeatures() {
    if (!this.selectedFeatures || this.selectedFeatures.getLength() === 0) {
      return false;
    }

    const features = this.selectedFeatures.getArray().slice();

    // 삭제 확인 (실수로 지우는 것을 방지)
    if (!confirm(`선택한 피처 ${features.length}개를 삭제할까요?\n이 작업은 되돌릴 수 없습니다.`)) {
      return false;
    }

    features.forEach(feature => {
      // 해당 피처가 속한 레이어 찾기
      const layers = layerManager.getAllLayers();

      for (const layerInfo of layers) {
        const source = layerInfo.source;
        if (!source) continue; // 흐름·래스터 레이어는 벡터 source 가 없다
        if (source.hasFeature(feature)) {
          source.removeFeature(feature);

          // 레이어의 피처 개수 업데이트
          layerInfo.featureCount = source.getFeatures().length;

          // 피처가 0개면 레이어 삭제 확인
          if (layerInfo.featureCount === 0) {
            if (confirm(`"${layerInfo.name}" 레이어에 피처가 없습니다. 레이어를 삭제할까요?`)) {
              layerManager.removeLayer(layerInfo.id);
            }
          }

          eventBus.emit(Events.FEATURE_DELETED, {
            feature,
            layerId: layerInfo.id
          });
          break;
        }
      }
    });

    // 선택 해제
    this.selectedFeatures.clear();

    // 상태 메시지
    const statusEl = document.getElementById('status-message');
    if (statusEl) {
      statusEl.textContent = '삭제되었습니다';
      setTimeout(() => {
        statusEl.textContent = '피처를 클릭하여 선택하세요';
      }, 2000);
    }

    // 레이어 패널 갱신
    eventBus.emit(Events.LAYER_ADDED, {});

    return true;
  }

  /**
   * 선택 해제
   */
  clearSelection() {
    if (this.selectedFeatures) {
      this.selectedFeatures.clear();
    }

    eventBus.emit(Events.FEATURE_DESELECTED, { features: [] });
  }

  /**
   * 선택 도구 비활성화
   */
  deactivate() {
    if (this.select) {
      this.map.removeInteraction(this.select);
      this.select = null;
    }

    if (this.modify) {
      this.map.removeInteraction(this.modify);
      this.modify = null;
    }

    if (this.dragBox) {
      this.map.removeInteraction(this.dragBox);
      this.dragBox = null;
    }

    // 패닝 복구
    if (this.dragPan) {
      this.dragPan.setActive(true);
      this.dragPan = null;
    }

    this.selectedFeatures = null;
    this.isActive = false;

    // 선택 0 → 툴바 버튼 숨김
    eventBus.emit(Events.SELECTION_CHANGED, { count: 0 });

    // 키보드 이벤트 해제
    document.removeEventListener('keydown', this.handleKeyDown);

    // 상태 메시지 초기화
    const statusEl = document.getElementById('status-message');
    if (statusEl) {
      statusEl.textContent = '준비';
    }

    eventBus.emit(Events.TOOL_DEACTIVATED, { tool: 'select' });
  }

  /**
   * 현재 활성화 상태 확인
   */
  getIsActive() {
    return this.isActive;
  }

  /**
   * 선택된 피처 반환 (그 시점의 사본)
   *
   * OL Collection 의 getArray() 는 살아 있는 배열이라, 받아 든 쪽이 그걸 들고 있는 동안
   * 선택이 바뀌면 배열이 발밑에서 변한다. 합치기가 '피처 2개를 1개로' 대신 '1개를 1개로'
   * 라고 말하던 것도, 선택을 비운 뒤에 개수를 읽어서 생긴 일이었다.
   * 순회하면서 원본을 지우는 코드도 있어서 사본으로 돌려준다.
   */
  getSelectedFeatures() {
    return this.selectedFeatures ? this.selectedFeatures.getArray().slice() : [];
  }


  /**
   * 선택된 피처 삭제 (alias)
   */
  deleteSelected() {
    return this.deleteSelectedFeatures();
  }

  /**
   * 현재 레이어의 모든 피처 선택
   */
  selectAll() {
    const selectedLayerId = layerManager.getSelectedLayerId();
    if (!selectedLayerId) {
      console.warn('레이어를 먼저 선택하세요');
      return;
    }

    const layerInfo = layerManager.getLayer(selectedLayerId);
    if (!layerInfo || !layerInfo.source) return; // 흐름·래스터 레이어는 선택할 피처가 없다

    // 선택 도구 활성화
    if (!this.isActive) {
      this.activate();
    }

    // 모든 피처 선택
    const features = layerInfo.source.getFeatures();
    if (this.selectedFeatures) {
      this.selectedFeatures.clear();
      features.forEach(feature => {
        this.selectedFeatures.push(feature);
      });
    }

    eventBus.emit(Events.FEATURE_SELECTED, {
      features: features,
      count: features.length
    });
  }
}

// 싱글톤 인스턴스
export const selectTool = new SelectTool();
